import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useWindows } from '../../contexts/WindowContext';
import { useSound } from '../../contexts/SoundContext';
import { useContextMenu } from '../../contexts/ContextMenuContext';
import { useLoading } from '../../contexts/LoadingContext';
import { useDesktopIcons, gridToPixel, pixelToGrid, getMaxGridDimensions } from '../../contexts/DesktopIconsContext';
import { Icon } from '../Icons';
import { EXTERNAL_LINK_APPS } from '../../utils/constants';
import type { AppType, ContextMenuItem, Position } from '../../types';
import styles from './DesktopIcon.module.css';

interface DesktopIconProps {
  id: string;
  name: string;
  icon: string;
  appType: AppType;
  iconSize?: number;
}

export const DesktopIcon: React.FC<DesktopIconProps> = ({
  id,
  name,
  icon,
  appType,
  iconSize = 58,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 });
  const [dragPosition, setDragPosition] = useState<Position | null>(null);

  const { openWindow } = useWindows();
  const { playSound } = useSound();
  const { showContextMenu } = useContextMenu();
  const { startLoading, stopLoading } = useLoading();
  const {
    getIconPosition,
    updateMultipleIconPositions,
    getIconAtPosition,
    selectedIcons,
    selectIcon,
    clearSelection,
    isSelected: checkIsSelected,
  } = useDesktopIcons();

  const iconRef = useRef<HTMLButtonElement>(null);
  const dragStartPos = useRef<Position>({ x: 0, y: 0 });
  const dragStartGridPositions = useRef<Map<string, Position>>(new Map());

  const gridPosition = getIconPosition(id);
  const pixelPosition = gridToPixel(gridPosition);
  const isSelected = checkIsSelected(id);

  // Launch app with loading cursor
  const launchApp = useCallback(() => {
    // Check if this is an external link app
    const externalUrl = EXTERNAL_LINK_APPS[appType];
    if (externalUrl) {
      playSound('click');
      window.open(externalUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    startLoading();
    setTimeout(() => {
      openWindow(appType);
      playSound('click');
      stopLoading();
    }, 150);
  }, [appType, openWindow, playSound, startLoading, stopLoading]);

  // Icon context menu items
  const getIconMenuItems = useCallback((): ContextMenuItem[] => [
    {
      id: 'open',
      label: 'Open',
      icon: icon,
      action: launchApp,
    },
    { id: 'divider-1', label: '', divider: true },
    {
      id: 'rename',
      label: 'Rename',
      disabled: true,
    },
    { id: 'divider-2', label: '', divider: true },
    {
      id: 'properties',
      label: 'Properties',
      icon: 'settings',
      disabled: true,
    },
  ], [icon, launchApp]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Select this icon if not already selected
    if (!isSelected) {
      selectIcon(id, false);
    }
    showContextMenu(
      { x: e.clientX, y: e.clientY },
      getIconMenuItems(),
      'icon',
      appType
    );
  }, [showContextMenu, getIconMenuItems, appType, isSelected, selectIcon, id]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDragging) return;

    // On mobile, single tap opens the app
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      launchApp();
      clearSelection();
      return;
    }

    // Ctrl+click to add/toggle selection
    if (e.ctrlKey || e.metaKey) {
      selectIcon(id, true);
    } else {
      selectIcon(id, false);
    }
    playSound('click');
  }, [playSound, isDragging, id, selectIcon, launchApp, clearSelection]);

  const handleDoubleClick = useCallback(() => {
    // On mobile, handleClick already opens the app — skip
    if (window.innerWidth < 768) return;
    if (!isDragging) {
      launchApp();
      clearSelection();
    }
  }, [launchApp, isDragging, clearSelection]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      launchApp();
    }
  }, [launchApp]);

  // Drag handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return; // Only left click

    const rect = iconRef.current?.getBoundingClientRect();
    if (!rect) return;

    // If this icon is not selected, select only this one (unless Ctrl is held)
    if (!isSelected) {
      if (e.ctrlKey || e.metaKey) {
        selectIcon(id, true);
      } else {
        selectIcon(id, false);
      }
    }

    dragStartPos.current = { x: e.clientX, y: e.clientY };
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });

    // Store initial grid positions of all selected icons for group drag
    dragStartGridPositions.current = new Map();
    // We need to include this icon in the drag even if it wasn't selected before
    const iconsToMove = isSelected ? selectedIcons : new Set([id]);
    iconsToMove.forEach(iconId => {
      dragStartGridPositions.current.set(iconId, getIconPosition(iconId));
    });

    // Don't start dragging immediately - wait for movement
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const dx = Math.abs(moveEvent.clientX - dragStartPos.current.x);
      const dy = Math.abs(moveEvent.clientY - dragStartPos.current.y);

      // Start dragging if moved more than 5 pixels
      if (dx > 5 || dy > 5) {
        setIsDragging(true);
        document.removeEventListener('mousemove', handleMouseMove);
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [isSelected, selectedIcons, id, selectIcon, getIconPosition]);

  // Handle dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      setDragPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      });
    };

    const handleMouseUp = (e: MouseEvent) => {
      const newPixelPosition = {
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      };

      // Convert to grid position delta
      const newGridPosition = pixelToGrid(newPixelPosition);
      const originalGridPosition = dragStartGridPositions.current.get(id) || gridPosition;
      const gridDelta = {
        x: newGridPosition.x - originalGridPosition.x,
        y: newGridPosition.y - originalGridPosition.y,
      };

      const { maxCols, maxRows } = getMaxGridDimensions();

      // Get all icons being moved (from the drag start)
      const iconsToMove = Array.from(dragStartGridPositions.current.entries());

      // Calculate new positions for all selected icons
      const updates: { id: string; gridPosition: Position }[] = [];
      let canMove = true;

      for (const [iconId, startPos] of iconsToMove) {
        let targetPos = {
          x: startPos.x + gridDelta.x,
          y: startPos.y + gridDelta.y,
        };

        // Clamp to valid grid boundaries
        targetPos = {
          x: Math.max(0, Math.min(maxCols - 1, targetPos.x)),
          y: Math.max(0, Math.min(maxRows - 1, targetPos.y)),
        };

        // Check if position is occupied by another icon (not in our group)
        const occupyingIcon = getIconAtPosition(targetPos);
        if (occupyingIcon && !dragStartGridPositions.current.has(occupyingIcon)) {
          // Position is occupied by an icon not being moved, try to find free position
          const freePosition = findNearestFreePosition(targetPos, iconId, iconsToMove.map(([id]) => id));
          if (freePosition) {
            targetPos = freePosition;
          } else {
            // Can't find a free position, cancel the move for this icon
            canMove = false;
            break;
          }
        }

        updates.push({ id: iconId, gridPosition: targetPos });
      }

      if (canMove && updates.length > 0) {
        updateMultipleIconPositions(updates);
      }

      setIsDragging(false);
      setDragPosition(null);
      playSound('click');
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, id, gridPosition, updateMultipleIconPositions, getIconAtPosition, playSound]);

  // Find nearest free grid position
  const findNearestFreePosition = (target: Position, excludeId: string, excludeIds: string[] = []): Position | null => {
    const { maxCols, maxRows } = getMaxGridDimensions();
    const allExcluded = new Set([excludeId, ...excludeIds]);

    // Search in expanding squares around target
    for (let radius = 1; radius <= Math.max(maxCols, maxRows); radius++) {
      for (let dx = -radius; dx <= radius; dx++) {
        for (let dy = -radius; dy <= radius; dy++) {
          if (Math.abs(dx) !== radius && Math.abs(dy) !== radius) continue; // Only check perimeter

          const checkPos = { x: target.x + dx, y: target.y + dy };

          // Check bounds
          if (checkPos.x < 0 || checkPos.x >= maxCols || checkPos.y < 0 || checkPos.y >= maxRows) {
            continue;
          }

          // Check if free
          const occupying = getIconAtPosition(checkPos);
          if (!occupying || allExcluded.has(occupying)) {
            return checkPos;
          }
        }
      }
    }
    return null;
  };

  // Calculate display position
  const displayPosition = isDragging && dragPosition
    ? dragPosition
    : pixelPosition;

  return (
    <button
      ref={iconRef}
      className={`${styles.desktopIcon} ${isSelected ? styles.selected : ''} ${isDragging ? styles.dragging : ''}`}
      style={{
        position: 'absolute',
        left: displayPosition.x,
        top: displayPosition.y,
        zIndex: isDragging ? 1000 : 1,
        opacity: isDragging ? 0.8 : 1,
        cursor: isDragging ? 'grabbing' : 'pointer',
      }}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      onContextMenu={handleContextMenu}
      onMouseDown={handleMouseDown}
      aria-label={name}
      title={name}
    >
      <div className={styles.iconWrapper} style={{ width: iconSize, height: iconSize }}>
        <Icon name={icon} size={iconSize} />
      </div>
      <span className={styles.label}>{name}</span>
    </button>
  );
};

export default DesktopIcon;
